import {Dispatch, memo, useRef} from 'react';

import styled from 'styled-components';

import {bodyBold, headingXS} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import {AuthFlowStep, ViewModes} from '~/shared/components/SignIn/consts';
import {TabOrangeLine} from '~/shared/components/TabOrangeLine';
import PieButton from '~/shared/theme/pie/PieButton';
import {flipOnLTR} from '~/shared/theme/utils';
import CloseIcon from '~/assets/images/icons/pie/functionalityCloseSmall.svg?react';
import ArrowIcon from '~/assets/images/icons/arrow_left_carousel.svg?react';

const Root = styled.div<{isTabMode?: boolean}>`
  position: relative;
  width: 100%;
  height: 72px;
  padding: ${({isTabMode}) => (isTabMode ? '28px 72px 0' : '22px 0')};
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 16px 16px 0 0;
  text-align: center;
`;

const StyledArrowRightIcon = styled(ArrowIcon)`
  ${({theme}) => !theme.isLTR && 'transform: rotate(180deg);'};
`;

const HeaderText = styled.p`
  ${headingXS}
`;

const Tab = styled.button`
  width: 177px;
  display: flex;
  justify-content: center;
  padding: 0;
  text-align: unset;
`;

const TabText = styled.p`
  ${bodyBold}
  color: ${({theme}) => theme.colors.contentDefault};
`;

const HeaderSideButton = styled(PieButton).attrs({
  variant: 'secondarySmall',
  iconButton: true,
})<{isTabMode?: boolean}>`
  position: absolute;
  top: 20px;
  ${({isTabMode}) => (isTabMode ?
    flipOnLTR`
      left: 20px;
    ` : flipOnLTR`
      right: 20px;
    `)
}
`;

type LoginOrRegisterHeaderProps = {
  viewMode: ViewModes;
  authFlowStep: AuthFlowStep;
  setAuthFlowStep: Dispatch<AuthFlowStep>;
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
  hideHeader?: boolean;
  onBackButtonClick?: () => void;
};

const LoginOrRegisterHeader = ({
  viewMode,
  authFlowStep,
  setAuthFlowStep,
  onSignInClick,
  onSignUpClick,
  hideHeader,
  onBackButtonClick,
}: LoginOrRegisterHeaderProps) => {
  const {t} = getLocalizationService();
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const currentIndex = viewMode === ViewModes.SIGN_IN_MODE ?
    0 :
    viewMode === ViewModes.SIGN_UP_MODE ?
      1 :
      null;

  if (hideHeader) {
    return null;
  }

  if (authFlowStep !== AuthFlowStep.INITIAL) {
    return (
      <Root>
        <HeaderText id="modal-title">
          {authFlowStep === AuthFlowStep.ADD_ADDRESS ? t('where_are_we_sending_the_food') : t('code_n_go')}
        </HeaderText>
        {authFlowStep !== AuthFlowStep.ADD_ADDRESS && (
          <HeaderSideButton
            onClick={() => {
              setAuthFlowStep(AuthFlowStep.INITIAL);
            }}
          >
            <StyledArrowRightIcon />
          </HeaderSideButton>
        )}
      </Root>
    );
  }

  return (
    <Root isTabMode>
      <Tab
        ref={el => {
          tabsRef.current[0] = el;
        }}
        onClick={onSignInClick}
      >
        <TabText>
          {t('connect')}
        </TabText>
      </Tab>
      <Tab
        ref={el => {
          tabsRef.current[1] = el;
        }}
        onClick={onSignUpClick}
      >
        <TabText>
          {t('register')}
        </TabText>
      </Tab>
      {typeof currentIndex === 'number' && <TabOrangeLine elsRefs={tabsRef} currentIndex={currentIndex} />}
      <HeaderSideButton onClick={onBackButtonClick} isTabMode>
        <CloseIcon />
      </HeaderSideButton>
    </Root>
  );
};

export default memo(LoginOrRegisterHeader);
